import { useState, useEffect } from 'react';
import { CheckCircle, FileText, Shield, ArrowRight, ExternalLink, AlertTriangle, Download } from 'lucide-react';
import { getVisitorId } from '../../utils/visitorTracking';
import { getTurnstileToken } from '../../utils/turnstileToken';
import SeoMeta from '../../components/SeoMeta';

const YouTubeVideo = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const videoId = isMobile ? '3S2iU-elBhc' : 'sgKsRngHljs';
  const aspectRatio = isMobile ? '9/16' : '16/9';

  return (
    <div className="max-w-3xl mx-auto" style={{ position: 'relative', overflow: 'hidden', aspectRatio }}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        loading="lazy"
        title="Documento de Control Digital - BlueCMR"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, border: 'none' }}
      />
    </div>
  );
};

const DocumentoControlDigital2026 = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    requestType: '',
    honeypot: ''
  });
  const [timestamp, setTimestamp] = useState(0);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmit, setLastSubmit] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    setTimestamp(Date.now());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = Date.now();
    const timeSinceLoad = now - timestamp;
    const timeSinceLastSubmit = now - lastSubmit;

    if (formData.honeypot) return;

    if (timeSinceLastSubmit < 5000) {
      setNotification({ type: 'warning', message: 'Por favor, espera unos segundos antes de enviar otro mensaje.' });
      return;
    }

    if (timeSinceLoad < 3000) {
      setNotification({ type: 'warning', message: 'Por favor, tómate un momento para revisar el formulario.' });
      return;
    }

    if (submitAttempts >= 3) {
      setNotification({ type: 'error', message: 'Demasiados intentos. Por favor, recarga la página.' });
      return;
    }

    setLoading(true);
    setSubmitAttempts(prev => prev + 1);
    setLastSubmit(now);

    try {
      const visitorId = getVisitorId();

      const response = await fetch('https://cynzv51q56.execute-api.eu-south-2.amazonaws.com/dev/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          subject: formData.requestType === 'trial' ? 'trial-landing-dca-2026' : 'landing-documento-control-2026',
          message: `${formData.requestType === 'trial' ? 'Solicitud de periodo de prueba' : 'Solicitud de demo'} desde landing Documento Control Digital 2026\nTeléfono: ${formData.phone || 'No proporcionado'}`,
          timestamp,
          timeSinceLoad,
          turnstileToken: await getTurnstileToken(),
          source: 'landing-documento-control-2026',
          visitorId: visitorId
        })
      });

      if (response.ok) {
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-16477839671/4tXdCKXNhOEZEMCYhPo9',
            'value': 1.0,
            'currency': 'EUR'
          });
        }
        setFormData({ name: '', company: '', email: '', phone: '', requestType: '', honeypot: '' });
        setSubmitted(true);
      } else {
        throw new Error('Error al enviar');
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al enviar el formulario. Por favor, inténtalo de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de confirmación tras envío
  if (submitted) {
    return (
      <>
        <SeoMeta
          title="Solicitud recibida - Documento de Control Digital 2026 | BlueCMR"
          description="Hemos recibido tu solicitud sobre el Documento de Control Digital obligatorio en 2026."
          canonical="https://bluecmr.com/landing/documento-control-digital-2026"
          noindex={true}
        />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-3xl w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  Solicitud recibida correctamente
                </h1>
                <p className="text-lg text-gray-600">
                  Hemos recibido tu solicitud. Un especialista te contactará en breve para explicarte cómo cumplir con la normativa.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm font-medium text-gray-700 mb-4">
                  Mientras tanto, puedes consultar más información:
                </p>
                <div className="space-y-3">
                  <a
                    href="/guias/cumplimiento-documento-control-2026"
                    className="flex items-center gap-3 p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                  >
                    <Download className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="font-semibold text-gray-900 text-sm">Guía gratuita: Cumplimiento del Documento de Control 2026</p>
                    <ArrowRight className="w-4 h-4 text-blue-400 ml-auto" />
                  </a>
                  <a
                    href="/documentos/documento-control"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="font-semibold text-gray-900 text-sm">Documento de Control Digital</p>
                    <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                  </a>
                  <a
                    href="/documentos/ecmr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="font-semibold text-gray-900 text-sm">eCMR - Carta de Porte Digital</p>
                    <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                  </a>
                  <a
                    href="/plataforma"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <p className="font-semibold text-gray-900 text-sm">Cómo funciona BlueCMR</p>
                    <ExternalLink className="w-4 h-4 text-gray-400 ml-auto" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SeoMeta
        title="Documento de Control Digital obligatorio desde el 5 de octubre de 2026 | BlueCMR"
        description="Desde el 5 de octubre de 2026 el Documento de Control Digital es obligatorio para transportes nacionales. Puede llevarse de forma independiente o ser sustituido por una Carta de Porte Digital. Solicita tu demo gratuita."
        canonical="https://bluecmr.com/landing/documento-control-digital-2026"
        noindex={true}
      />

      <div className="min-h-screen bg-gray-50">

        {/* HERO: Contexto de la situación */}
        <section className="bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 text-white py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/40 text-yellow-200 text-sm font-semibold px-4 py-2 rounded-full mb-8">
              <AlertTriangle className="w-4 h-4" />
              Obligatorio desde el 5 de octubre de 2026
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              El Documento de Control Digital<br className="hidden md:block" /> ya es obligatorio
            </h1>

            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              La Ley de Movilidad Sostenible exige que el documento de control de transporte de mercancías por carretera sea en formato electrónico desde el 5 de octubre de 2026.
            </p>

            <button
              onClick={() => document.getElementById('formulario')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors text-lg"
            >
              Solicitar demo gratuita
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* SECCIÓN: Las dos opciones */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                ¿Cómo se puede cumplir?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Existen dos formas de cumplir con la obligación del documento de control digital:
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Opción 1: Documento de Control independiente */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Lo más sencillo</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Documento de Control independiente
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  El documento de control digital se lleva de forma independiente, con los datos obligatorios del transporte según la Orden FOM/2861/2012.
                </p>
                <a
                  href="/documentos/documento-control"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                >
                  Más información
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>

              {/* Opción 2: eCMR que sustituye */}
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-blue-300 transition-colors">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-5">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">Lo más completo</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  eCMR o Carta de Porte Digital
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Si la carta de porte digital incluye todos los datos obligatorios del documento de control, puede sustituirlo en transportes nacionales. Un solo documento para todo.
                </p>
                <a
                  href="/documentos/ecmr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                >
                  Más información
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECCIÓN: Vídeo */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Conoce el Documento de Control Digital
              </h2>
              <p className="text-gray-600">
                Te explicamos en vídeo qué es y cómo funciona
              </p>
            </div>
            <YouTubeVideo />
          </div>
        </section>

        {/* SECCIÓN: Guía gratuita */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <a
              href="/guias/cumplimiento-documento-control-2026"
              className="flex flex-col sm:flex-row items-center gap-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 sm:p-8 hover:shadow-lg transition-shadow group"
            >
              <div className="relative flex-shrink-0">
                <img
                  src="/guides/portada-guia-cumplimiento-2026.png"
                  alt="Portada: Guía de Cumplimiento del Documento de Control Digital 2026"
                  className="w-32 rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-md">
                  <Download className="w-4 h-4 text-white" />
                </div>
              </div>
              <div className="text-center sm:text-left flex-1">
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Guía gratuita en PDF</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                  Guía práctica: Cumplimiento del Documento de Control Digital en 2026
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Normativa, requisitos técnicos, diferencias entre documentos, cronograma eFTI y checklist de cumplimiento.
                </p>
                <span className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all">
                  Descargar gratis
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </a>
          </div>
        </section>

        {/* Test normativo */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 my-12">
          <a href="/test-normativo" className="block bg-blue-50 border border-blue-200 rounded-xl p-6 hover:bg-blue-100 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-lg mb-1">¿Estás preparado para el cambio normativo?</h3>
                <p className="text-gray-600 text-sm">Comprueba tu nivel de conocimiento con nuestro test interactivo de 12 preguntas</p>
              </div>
              <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0" />
            </div>
          </a>
        </div>

        {/* SECCIÓN: BlueCMR como solución */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                En BlueCMR generamos todos estos documentos
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Ofrecemos a cargadores y transportistas la posibilidad de generar el documento de control, la carta de porte digital y el eCMR desde una única plataforma.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              <a
                href="/documentos/documento-control"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">Documento de Control</h3>
                <p className="text-sm text-gray-600 mb-3">Formato digital conforme a la normativa</p>
                <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium">
                  Ver más <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </a>
              <a
                href="/documentos/carta-porte"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">Carta de Porte Digital</h3>
                <p className="text-sm text-gray-600 mb-3">Documento contractual nacional</p>
                <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium">
                  Ver más <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </a>
              <a
                href="/documentos/ecmr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-center p-6 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">eCMR</h3>
                <p className="text-sm text-gray-600 mb-3">Carta de porte internacional digital</p>
                <span className="inline-flex items-center gap-1 text-sm text-blue-600 font-medium">
                  Ver más <ExternalLink className="w-3.5 h-3.5" />
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* SECCIÓN: Formulario */}
        <section id="formulario" className="py-16 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Solicitar información
                </h2>
                <p className="text-gray-600">
                  Descubre cómo digitalizar tus documentos de control
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Seleccione una de las opciones:</label>
                <div className="flex gap-3 bg-gray-50 p-2 rounded-xl">
                  <button type="button" onClick={() => setFormData({...formData, requestType: 'demo'})}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${formData.requestType === 'demo' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:shadow-sm'}`}>
                    Solicitar demo
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, requestType: 'trial'})}
                    className={`flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${formData.requestType === 'trial' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-600 hover:bg-white hover:shadow-sm'}`}>
                    Periodo de prueba
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="honeypot"
                  value={formData.honeypot}
                  onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                  style={{position: 'absolute', left: '-9999px', opacity: 0}}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa *
                  </label>
                  <input
                    type="text"
                    id="company"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email corporativo *
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? 'Enviando...' : formData.requestType === 'trial' ? 'Solicitar periodo de prueba' : 'Solicitar demo'}
                  {!loading && <ArrowRight className="w-5 h-5" />}
                </button>

                {notification && (
                  <div className={`p-4 rounded-lg ${
                    notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                    'bg-yellow-50 text-yellow-800 border border-yellow-200'
                  }`}>
                    <div className="flex justify-between items-start">
                      <p>{notification.message}</p>
                      <button
                        onClick={() => setNotification(null)}
                        className="ml-4 text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 text-center">
                  Al enviar este formulario, aceptas nuestra{' '}
                  <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    política de privacidad
                  </a>
                </p>
              </form>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default DocumentoControlDigital2026;
